import { useState, useMemo, useCallback, useEffect } from "react";
import {
  getProducts,
  getAssignProductivityLevel,
  assignProductivityLevel,
  getAssignCleaningLevel,
  assignCleaningLevel,
  getAssignMachineToolLevel,
  assignMachineToolLevel,
  updateProduct,
} from "../api/ProductsApis";
import { rooms } from "../constants/rooms";
import {
  CheckCircleIcon,
  TrashIcon,
  PaperAirplaneIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  DocumentTextIcon as EmptyDocumentIcon,
} from "@heroicons/react/24/outline";
import RoomProductivityConfig from "./RoomProductivityConfig";
import RoomCleaningConfig from "./RoomCleaningConfig";
import RoomMachineToolConfig from "./RoomMachineToolConfig";
import toast, { Toaster } from "react-hot-toast";
import {
  PRODUCTIVITY_OPTIONS,
  CLEANING_OPTIONS,
  MACHINE_TOOL_OPTIONS,
  DEFAULT_PRODUCTIVITY,
  DEFAULT_CLEANING,
  DEFAULT_MACHINE_TOOL,
} from "../constants/productConfigOptions";
import useDebounce from "../hooks/useDebounce";
import { useAuth } from "../context/AuthContext";

const ProductList = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [filters, setFilters] = useState({ sku: "", title: "" });
  const debouncedFilters = useDebounce(filters, 300);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [assignments, setAssignments] = useState({});
  const [dropdowns, setDropdowns] = useState({
    productivity: "",
    cleaning: "",
    machineTool: "",
  });
  const [roomProductivity, setRoomProductivity] = useState([]);
  const [isLoadingProductivity, setIsLoadingProductivity] = useState(true);
  const [productivityError, setProductivityError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [roomCleaning, setRoomCleaning] = useState([]);
  const [isLoadingCleaning, setIsLoadingCleaning] = useState(true);
  const [cleaningError, setCleaningError] = useState(null);
  const [isCleaningEditMode, setIsCleaningEditMode] = useState(false);
  const [roomMachineTool, setRoomMachineTool] = useState([]);
  const [isLoadingMachineTool, setIsLoadingMachineTool] = useState(true);
  const [machineToolError, setMachineToolError] = useState(null);
  const [isMachineToolEditMode, setIsMachineToolEditMode] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [draftRoomProductivity, setDraftRoomProductivity] = useState([]);
  const [originalProductivityBackup, setOriginalProductivityBackup] =
    useState(null);
  const [draftRoomCleaning, setDraftRoomCleaning] = useState([]);
  const [originalCleaningBackup, setOriginalCleaningBackup] = useState(null);
  const [draftRoomMachineTool, setDraftRoomMachineTool] = useState([]);
  const [originalMachineToolBackup, setOriginalMachineToolBackup] =
    useState(null);

  const itemsPerPage = 35;
  const { canWrite } = useAuth();
  const canManageProductConfig = canWrite("ERPagProducts");
  const toMachineToolKey = (label) =>
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  const ensureAllRooms = useCallback((apiData = []) => {
    const apiRoomMap = new Map(
      apiData.map((item) => [item.room.toLowerCase(), item]),
    );
    return rooms.map((roomName) => {
      const apiRoom = apiRoomMap.get(roomName.toLowerCase());
      if (apiRoom) {
        return {
          _id: apiRoom._id,
          room: apiRoom.room,
          productivity: apiRoom.productivity,
        };
      }
      return {
        room: roomName,
        productivity: { ...DEFAULT_PRODUCTIVITY },
      };
    });
  }, []);

  const ensureAllRoomsForCleaning = useCallback((apiData = []) => {
    const apiRoomMap = new Map(
      apiData.map((item) => [item.room.toLowerCase(), item]),
    );
    return rooms.map((roomName) => {
      const apiRoom = apiRoomMap.get(roomName.toLowerCase());
      if (apiRoom && apiRoom.cleaning) {
        return {
          _id: apiRoom._id,
          room: apiRoom.room,
          cleaning: apiRoom.cleaning,
        };
      }
      return {
        room: roomName,
        cleaning: { ...DEFAULT_CLEANING },
      };
    });
  }, []);

  const buildMachineToolDefaults = useCallback((roomName) => {
    const defaults = { ...DEFAULT_MACHINE_TOOL };
    if (roomName === "Room 5 Bags") {
      defaults.long_for_bagging = 1;
    }
    return defaults;
  }, []);

  const ensureAllRoomsForMachineTool = useCallback((apiData = []) => {
    const apiRoomMap = new Map(
      apiData.map((item) => [item.room.toLowerCase(), item]),
    );
    return rooms.map((roomName) => {
      const defaults = buildMachineToolDefaults(roomName);
      const apiRoom = apiRoomMap.get(roomName.toLowerCase());
      if (apiRoom && apiRoom.machineTool) {
        const mergedMachineTool = {
          ...defaults,
          ...apiRoom.machineTool,
        };
        if (apiRoom.machineTool.long_for_bagging == null) {
          mergedMachineTool.long_for_bagging = defaults.long_for_bagging;
        }
        return {
          _id: apiRoom._id,
          room: apiRoom.room,
          machineTool: mergedMachineTool,
        };
      }
      return {
        room: roomName,
        machineTool: defaults,
      };
    });
  }, [buildMachineToolDefaults]);

  useEffect(() => {
    const fetchRoomProductivity = async () => {
      try {
        setIsLoadingProductivity(true);
        setProductivityError(null);
        const response = await getAssignProductivityLevel();
        const transformedData = ensureAllRooms(response.data);
        setRoomProductivity(transformedData);
      } catch (err) {
        console.error("Failed to fetch room productivity:", err);
        setProductivityError(
          "Failed to load some room productivity data from server. Using default values for missing rooms.",
        );
        const allRoomsWithDefaults = ensureAllRooms([]);
        setRoomProductivity(allRoomsWithDefaults);
      } finally {
        setIsLoadingProductivity(false);
      }
    };
    fetchRoomProductivity();
  }, [ensureAllRooms]);

  useEffect(() => {
    const fetchRoomCleaning = async () => {
      try {
        setIsLoadingCleaning(true);
        setCleaningError(null);
        const response = await getAssignCleaningLevel();
        const transformedData = ensureAllRoomsForCleaning(response.data);
        setRoomCleaning(transformedData);
      } catch (err) {
        console.error("Failed to fetch room cleaning:", err);
        setCleaningError(
          "Failed to load cleaning data. Using default times for all rooms.",
        );
        const allRoomsWithDefaults = ensureAllRoomsForCleaning([]);
        setRoomCleaning(allRoomsWithDefaults);
      } finally {
        setIsLoadingCleaning(false);
      }
    };
    fetchRoomCleaning();
  }, [ensureAllRoomsForCleaning]);

  useEffect(() => {
    const fetchRoomMachineTool = async () => {
      try {
        setIsLoadingMachineTool(true);
        setMachineToolError(null);
        const response = await getAssignMachineToolLevel();
        const transformedApiData = response.data.map((item) => {
          const machineTool = {
            long_screw_small: item.machine_tool.longScrewSmall,
            long_screw_medium: item.machine_tool.longScrewMedium,
            long_screw_large: item.machine_tool.longScrewLarge,
            short_small: item.machine_tool.shortSmall,
            short_large: item.machine_tool.shortLarge,
          };
          if (item.machine_tool?.longForBagging != null) {
            machineTool.long_for_bagging = item.machine_tool.longForBagging;
          }
          return {
            _id: item._id,
            room: item.room,
            machineTool,
          };
        });
        const transformedData =
          ensureAllRoomsForMachineTool(transformedApiData);
        setRoomMachineTool(transformedData);
      } catch (err) {
        console.error("Failed to fetch room machine tool:", err);
        setMachineToolError(
          "Failed to load machine tool data. Using default times for all rooms.",
        );
        const allRoomsWithDefaults = ensureAllRoomsForMachineTool([]);
        setRoomMachineTool(allRoomsWithDefaults);
      } finally {
        setIsLoadingMachineTool(false);
      }
    };
    fetchRoomMachineTool();
  }, [ensureAllRoomsForMachineTool]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await getProducts();
        const transformedProducts = response.data.map((product) => ({
          _id: product._id,
          sku: product.sku,
          title: product.description,
          productivity_level: product.productivity_level || "",
          cleaning_level: product.cleaning_level || "",
          machineTool: product.machineTool || "",
        }));
        setProducts(transformedProducts);
        setIsLoading(false);
      } catch (err) {
        setError("Failed to fetch products. Please try again.");
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleToggleEditMode = useCallback(() => {
    if (!isEditMode) {
      setOriginalProductivityBackup([...roomProductivity]);
      setDraftRoomProductivity([...roomProductivity]);
      setIsEditMode(true);
    } else {
      if (originalProductivityBackup) {
        setRoomProductivity([...originalProductivityBackup]);
      }
      setDraftRoomProductivity([]);
      setOriginalProductivityBackup(null);
      setIsEditMode(false);
    }
  }, [isEditMode, roomProductivity, originalProductivityBackup]);

  const handleSaveRoomConfig = useCallback(async () => {
    try {
      await assignProductivityLevel(draftRoomProductivity);

      setRoomProductivity([...draftRoomProductivity]);
      setOriginalProductivityBackup(null);
      setDraftRoomProductivity([]);
      setIsEditMode(false);

      toast.success("Productivity levels updated successfully.", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving room config:", error);
      // On error, restore from backup
      if (originalProductivityBackup) {
        setRoomProductivity([...originalProductivityBackup]);
      }
      toast.error("Failed to save room configuration.", {
        duration: 3000,
      });
    }
  }, [draftRoomProductivity, originalProductivityBackup]);

  const handleProductivityUpdate = useCallback(
    (roomName, level, newValue) => {
      if (!isEditMode) return;
      setDraftRoomProductivity((prev) =>
        prev.map((roomConfig) =>
          roomConfig.room === roomName
            ? {
                ...roomConfig,
                productivity: {
                  ...roomConfig.productivity,
                  [level.toLowerCase()]: parseInt(newValue) || 0,
                },
              }
            : roomConfig,
        ),
      );
    },
    [isEditMode],
  );

  const handleToggleCleaningEditMode = useCallback(() => {
    if (!isCleaningEditMode) {
      setOriginalCleaningBackup([...roomCleaning]);
      setDraftRoomCleaning([...roomCleaning]);
      setIsCleaningEditMode(true);
    } else {
      if (originalCleaningBackup) {
        setRoomCleaning([...originalCleaningBackup]);
      }
      setDraftRoomCleaning([]);
      setOriginalCleaningBackup(null);
      setIsCleaningEditMode(false);
    }
  }, [isCleaningEditMode, roomCleaning, originalCleaningBackup]);

  const handleSaveCleaningConfig = useCallback(async () => {
    try {
      await assignCleaningLevel(draftRoomCleaning);

      setRoomCleaning([...draftRoomCleaning]);

      setOriginalCleaningBackup(null);
      setDraftRoomCleaning([]);
      setIsCleaningEditMode(false);

      toast.success("Cleaning levels updated successfully.", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving cleaning config:", error);
      // On error, restore from backup
      if (originalCleaningBackup) {
        setRoomCleaning([...originalCleaningBackup]);
      }
      toast.error("Failed to save cleaning configuration.", {
        duration: 3000,
      });
    }
  }, [draftRoomCleaning, originalCleaningBackup]);

  const handleCleaningUpdate = useCallback(
    (roomIndex, cleaningType, newValue) => {
      if (!isCleaningEditMode) return;

      const timeValue = parseInt(newValue) || 0;

      // Update draft only, not main state
      setDraftRoomCleaning((prev) =>
        prev.map((roomConfig, index) =>
          index === roomIndex
            ? {
                ...roomConfig,
                cleaning: {
                  ...roomConfig.cleaning,
                  [cleaningType.toLowerCase()]: timeValue,
                },
              }
            : roomConfig,
        ),
      );
    },
    [isCleaningEditMode],
  );

  const handleToggleMachineToolEditMode = useCallback(() => {
    if (!isMachineToolEditMode) {
      // Entering edit mode - backup and copy to draft
      setOriginalMachineToolBackup([...roomMachineTool]);
      setDraftRoomMachineTool([...roomMachineTool]);
      setIsMachineToolEditMode(true);
    } else {
      // Exiting edit mode (Cancel) - restore from backup
      if (originalMachineToolBackup) {
        setRoomMachineTool([...originalMachineToolBackup]);
      }
      setDraftRoomMachineTool([]);
      setOriginalMachineToolBackup(null);
      setIsMachineToolEditMode(false);
    }
  }, [isMachineToolEditMode, roomMachineTool, originalMachineToolBackup]);

  const handleSaveMachineToolConfig = useCallback(async () => {
    try {
      // Work only with filtered data from the start
      const filteredDraft = draftRoomMachineTool.filter(
        (room) => !["Bulk Zone", "Manual Zone"].includes(room.room),
      );
      const sanitizedDraft = filteredDraft.map((roomConfig) => {
        const longForBaggingValue =
          roomConfig.room === "Room 5 Bags"
            ? roomConfig.machineTool.long_for_bagging ?? 1
            : 0;
        return {
          ...roomConfig,
          machineTool: {
            ...roomConfig.machineTool,
            long_for_bagging: longForBaggingValue,
          },
        };
      });

      const payload = sanitizedDraft.map((roomConfig) => ({
        room: roomConfig.room,
        machine_tool: {
          longScrewSmall: roomConfig.machineTool.long_screw_small,
          longScrewMedium: roomConfig.machineTool.long_screw_medium,
          longScrewLarge: roomConfig.machineTool.long_screw_large,
          shortSmall: roomConfig.machineTool.short_small,
          shortLarge: roomConfig.machineTool.short_large,
          longForBagging: roomConfig.machineTool.long_for_bagging,
        },
      }));

      await assignMachineToolLevel(payload);

      // IMPORTANT: Commit only the filtered version to main state
      setRoomMachineTool((prev) => {
        const filteredMap = new Map(sanitizedDraft.map((r) => [r.room, r]));
        return prev
          .filter((room) => !["Bulk Zone", "Manual Zone"].includes(room.room))
          .map((room) => filteredMap.get(room.room) || room);
      });

      // Also clear draft completely (no hidden rooms left)
      setDraftRoomMachineTool([]);
      setOriginalMachineToolBackup(null);
      setIsMachineToolEditMode(false);

      toast.success("Machine Tool levels updated successfully.", {
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving machine tool config:", error);
      if (originalMachineToolBackup) {
        setRoomMachineTool([...originalMachineToolBackup]);
      }
      toast.error("Failed to save machine tool configuration.", {
        duration: 3000,
      });
    }
  }, [draftRoomMachineTool, originalMachineToolBackup]);

  const handleMachineToolUpdate = useCallback(
    (roomIndex, toolType, newValue) => {
      if (!isMachineToolEditMode) return;

      const timeValue = parseInt(newValue) || 0;
      const key = toMachineToolKey(toolType);

      const filteredRooms = draftRoomMachineTool.filter(
        (room) => !["Bulk Zone", "Manual Zone"].includes(room.room),
      );
      const actualRoom = filteredRooms[roomIndex];
      if (!actualRoom) return;
      if (toolType === "Long-for-Bagging" && actualRoom.room !== "Room 5 Bags")
        return;

      const actualIndex = draftRoomMachineTool.findIndex(
        (r) => r.room === actualRoom.room,
      );

      setDraftRoomMachineTool((prev) =>
        prev.map((roomConfig, index) =>
          index === actualIndex
            ? {
                ...roomConfig,
                machineTool: {
                  ...roomConfig.machineTool,
                  [key]: timeValue,
                },
              }
            : roomConfig,
        ),
      );
    },
    [isMachineToolEditMode, draftRoomMachineTool],
  );

  const handleDropdownChange = useCallback((key, value) => {
    setDropdowns((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClearDropdowns = useCallback(() => {
    setDropdowns({
      productivity: "",
      cleaning: "",
      machineTool: "",
    });
    toast.success("Dropdown selections cleared.", {
      duration: 3000,
    });
  }, []);

  const handleAssignValues = useCallback(async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one SKU.", { duration: 3000 });
      return;
    }
    if (
      !dropdowns.productivity &&
      !dropdowns.cleaning &&
      !dropdowns.machineTool
    ) {
      toast.error("Please select at least one value to assign.", {
        duration: 3000,
      });
      return;
    }

    setIsAssigning(true);

    try {
      const payload = {
        ids: selectedProducts,
        productivity_level: dropdowns.productivity || undefined,
        cleaning_level: dropdowns.cleaning || undefined,
        machine_tool: dropdowns.machineTool || undefined,
      };

      Object.keys(payload).forEach(
        (key) => payload[key] === undefined && delete payload[key],
      );

      const response = await updateProduct(payload);

      // Update UI only on success
      setProducts((prev) =>
        prev.map((product) =>
          selectedProducts.includes(product.sku)
            ? {
                ...product,
                productivity_level:
                  dropdowns.productivity || product.productivity_level,
                cleaning_level: dropdowns.cleaning || product.cleaning_level,
                machineTool: dropdowns.machineTool || product.machineTool,
              }
            : product,
        ),
      );

      // Reset form
      setDropdowns({ productivity: "", cleaning: "", machineTool: "" });
      setSelectedProducts([]);

      toast.success(
        `Successfully updated ${response.modifiedCount} product(s).`,
        { duration: 3000 },
      );
    } catch (error) {
      console.error("Error updating products:", error);
      toast.error("Failed to update products. Please try again.", {
        duration: 3000,
      });
    } finally {
      setIsAssigning(false);
    }
  }, [selectedProducts, dropdowns]);

  const handleCheckboxChange = useCallback((sku) => {
    setSelectedProducts((prev) =>
      prev.includes(sku) ? prev.filter((id) => id !== sku) : [...prev, sku],
    );
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSKU = product.sku
        .toLowerCase()
        .includes(debouncedFilters.sku.toLowerCase());
      const matchesTitle = product.title
        .toLowerCase()
        .includes(debouncedFilters.title.toLowerCase());
      return matchesSKU && matchesTitle;
    });
  }, [debouncedFilters, products]); // Use debouncedFilters

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handleSelectAll = useCallback(() => {
    const currentProducts = filteredProducts.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
    if (
      currentProducts.every((product) => selectedProducts.includes(product.sku))
    ) {
      setSelectedProducts((prev) =>
        prev.filter((sku) => !currentProducts.some((p) => p.sku === sku)),
      );
    } else {
      setSelectedProducts((prev) => [
        ...prev,
        ...currentProducts
          .filter((product) => !prev.includes(product.sku))
          .map((product) => product.sku),
      ]);
    }
  }, [selectedProducts, filteredProducts, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const renderPagination = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 rounded-md text-sm ${
            currentPage === i
              ? "bg-gray-500 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>,
      );
    }
    return (
      <div className="flex justify-center items-center mt-4 space-x-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-700 disabled:opacity-50 hover:bg-gray-300 text-sm"
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-700 disabled:opacity-50 hover:bg-gray-300 text-sm"
        >
          Next
        </button>
      </div>
    );
  };

  if (isLoadingProductivity || isLoadingCleaning || isLoadingMachineTool) {
    return (
      <div className="bg-white p-5 sm:p-7 rounded-lg shadow-md border border-gray-200">
        <div className="text-center py-12">
          <ArrowPathIcon className="mx-auto h-8 w-8 text-blue-600 mb-4 animate-spin" />
          <p className="text-gray-600 text-sm">
            Loading room configuration data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 sm:p-7 rounded-lg shadow-md border border-gray-200">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-start gap-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Product List
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage and configure room productivity, cleaning, and machine tool
              settings across all locations.
            </p>
          </div>
        </div>
      </div>

      {productivityError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2 text-sm text-yellow-800">
            <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>{productivityError}</span>
          </div>
        </div>
      )}

      {cleaningError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2 text-sm text-yellow-800">
            <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>{cleaningError}</span>
          </div>
        </div>
      )}

      {machineToolError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2 text-sm text-yellow-800">
            <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>{machineToolError}</span>
          </div>
        </div>
      )}

      <RoomProductivityConfig
        roomProductivity={(isEditMode
          ? draftRoomProductivity
          : roomProductivity
        ).filter((room) => room.room !== "Bulk Zone")}
        isEditMode={isEditMode}
        productivityError={productivityError}
        onToggleEditMode={handleToggleEditMode}
        onSaveConfig={handleSaveRoomConfig}
        onProductivityUpdate={handleProductivityUpdate}
        canEdit={canManageProductConfig}
      />

      <div className="mt-8">
        <RoomCleaningConfig
          roomCleaning={(isCleaningEditMode
            ? draftRoomCleaning
            : roomCleaning
          ).filter((room) => !["Bulk Zone", "Manual Zone"].includes(room.room))}
          isEditMode={isCleaningEditMode}
          cleaningError={cleaningError}
          onToggleEditMode={handleToggleCleaningEditMode}
          onSaveConfig={handleSaveCleaningConfig}
          onCleaningUpdate={handleCleaningUpdate}
          cleaningOptions={CLEANING_OPTIONS}
          // canEdit={canManageProductConfig} 
          canEdit={true}
        />
      </div>

      <div className="mt-8">
        <RoomMachineToolConfig
          roomMachineTool={(isMachineToolEditMode
            ? draftRoomMachineTool
            : roomMachineTool
          ).filter((room) => !["Bulk Zone", "Manual Zone"].includes(room.room))}
          isEditMode={isMachineToolEditMode}
          machineToolError={machineToolError}
          onToggleEditMode={handleToggleMachineToolEditMode}
          onSaveConfig={handleSaveMachineToolConfig}
          onMachineToolUpdate={handleMachineToolUpdate}
          canEdit={canManageProductConfig}
        />
      </div>

      {/* MODIFIED: Separated filter section */}
      <div className="mb-6 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-3 h-3 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Product Filters
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Search products by SKU or title to narrow down the list.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label
              htmlFor="sku"
              className="text-sm font-medium text-gray-700 mb-2"
            >
              SKU Search
            </label>
            <input
              id="sku"
              type="text"
              value={filters.sku}
              onChange={(e) => handleFilterChange("sku", e.target.value)}
              placeholder="e.g., 100021021"
              className="w-full py-3 px-4 rounded-lg border border-gray-200 text-sm text-gray-900 transition-all duration-200 bg-gray-50 hover:bg-white"
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="title"
              className="text-sm font-medium text-gray-700 mb-2"
            >
              Product Title
            </label>
            <input
              id="title"
              type="text"
              value={filters.title}
              onChange={(e) => handleFilterChange("title", e.target.value)}
              placeholder="e.g., Bulk spices"
              className="w-full py-3 px-4 rounded-lg border border-gray-200 text-sm text-gray-900 transition-all duration-200 bg-gray-50 hover:bg-white"
            />
          </div>
        </div>
      </div>

      {/* MODIFIED: Integrated Assign button, Clear button, and counter into assignment section */}
      <div className="mb-6 bg-white p-6 rounded-xl shadow-sm border border-blue-200">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-3 h-3 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Assign Configurations
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Select values for Productivity, Cleaning, or Machine Tool to
              assign to selected products.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="flex flex-col">
            <label
              htmlFor="productivity"
              className="text-sm font-medium text-gray-700 mb-2"
            >
              Productivity Level
            </label>
            <div className="relative">
              <select
                id="productivity"
                value={dropdowns.productivity}
                onChange={(e) =>
                  handleDropdownChange("productivity", e.target.value)
                }
                className="w-full py-3 px-4 rounded-lg border border-gray-200 text-sm text-gray-900 transition-all duration-200 bg-gray-50 hover:bg-white appearance-none"
              >
                <option value="">Select Productivity</option>
                {PRODUCTIVITY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="cleaning"
              className="text-sm font-medium text-gray-700 mb-2"
            >
              Cleaning Type
            </label>
            <div className="relative">
              <select
                id="cleaning"
                value={dropdowns.cleaning}
                onChange={(e) =>
                  handleDropdownChange("cleaning", e.target.value)
                }
                className="w-full py-3 px-4 rounded-lg border border-gray-200 text-sm text-gray-900 transition-all duration-200 bg-gray-50 hover:bg-white appearance-none"
              >
                <option value="">Select Cleaning</option>
                {CLEANING_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 w-5 h-5 text-gray-400" />
            </div>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="machineTool"
              className="text-sm font-medium text-gray-700 mb-2"
            >
              Machine Tool
            </label>
            <div className="relative">
              <select
                id="machineTool"
                value={dropdowns.machineTool}
                onChange={(e) =>
                  handleDropdownChange("machineTool", e.target.value)
                }
                className="w-full py-3 px-4 rounded-lg border border-gray-200 text-sm text-gray-900 transition-all duration-200 bg-gray-50 hover:bg-white appearance-none"
              >
                <option value="">Select Machine Tool</option>
                {MACHINE_TOOL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          {selectedProducts.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-red-600 mb-3 sm:mb-0">
              <ExclamationTriangleIcon className="w-4 h-4" />
              <span>Please select at least one product to assign values</span>
            </div>
          )}
          {selectedProducts.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3 sm:mb-0">
              <CheckCircleIcon className="w-4 h-4 text-green-500" />
              <span>
                {selectedProducts.length}{" "}
                {selectedProducts.length === 1 ? "product" : "products"}{" "}
                selected
                {dropdowns.productivity ||
                dropdowns.cleaning ||
                dropdowns.machineTool ? (
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    ✓ Ready to assign
                  </span>
                ) : null}
              </span>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleAssignValues}
              disabled={
                isAssigning ||
                selectedProducts.length === 0 ||
                (!dropdowns.productivity &&
                  !dropdowns.cleaning &&
                  !dropdowns.machineTool) ||
                !canManageProductConfig
              }
              className="relative px-6 py-3 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200"
              title={
                isAssigning
                  ? "Assigning values in progress..."
                  : selectedProducts.length === 0
                    ? "Please select at least one product to assign values"
                    : !dropdowns.productivity &&
                        !dropdowns.cleaning &&
                        !dropdowns.machineTool
                      ? "Please select at least one configuration (Productivity, Cleaning, or Machine Tool)"
                      : !canManageProductConfig
                        ? "You do not have permission to assign product configurations"
                        : "Assign selected values to products"
              }
            >
              {isAssigning ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Assign Values ({selectedProducts.length})
                </>
              )}
            </button>
            <button
              onClick={handleClearDropdowns}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Clear Selections
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <ArrowPathIcon className="mx-auto h-8 w-8 text-blue-600 mb-4 animate-spin" />
          <p className="text-gray-600 text-sm">Loading products...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="hidden sm:block overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold text-gray-900 text-xs tracking-wider border-b">
                    <div className="flex items-center gap-2 relative group">
                      <input
                        type="checkbox"
                        checked={
                          paginatedProducts.length > 0 &&
                          paginatedProducts.every((p) =>
                            selectedProducts.includes(p.sku),
                          )
                        }
                        onChange={handleSelectAll}
                        disabled={!canManageProductConfig}
                        className={`h-4 w-4 rounded focus:ring-2 focus:ring-blue-500 ${
                          !canManageProductConfig
                            ? "opacity-50 cursor-not-allowed bg-gray-200"
                            : "cursor-pointer"
                        }`}
                      />
                      <span>Select All</span>

                      {!canManageProductConfig && (
                        <div className="absolute left-0 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          <div className="bg-gray-800 text-white text-xs px-2.5 py-1.5 rounded shadow whitespace-nowrap">
                            No permission to select products
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="py-4 px-4 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider border-b">
                    SKU
                  </th>
                  <th className="py-4 px-4 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider border-b">
                    Product Title
                  </th>
                  <th className="py-4 px-4 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider border-b">
                    Productivity
                  </th>
                  <th className="py-4 px-4 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider border-b">
                    Cleaning
                  </th>
                  <th className="py-4 px-4 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider border-b">
                    Machine Tool
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.length > 0 ? (
                  paginatedProducts.map((product) => (
                    <tr
                      key={product.sku}
                      className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                        selectedProducts.includes(product.sku)
                          ? "bg-blue-50"
                          : "bg-white"
                      }`}
                    >
                      {/* Desktop table view */}
                      <td className="py-4 px-6">
                        <div className="relative group">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.sku)}
                            onChange={() => handleCheckboxChange(product.sku)}
                            disabled={!canManageProductConfig}
                            className={`h-4 w-4 rounded focus:ring-2 focus:ring-blue-500 ${
                              !canManageProductConfig
                                ? "opacity-50 cursor-not-allowed bg-gray-200"
                                : "cursor-pointer"
                            }`}
                          />
                          {!canManageProductConfig && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              <span className="text-xs bg-gray-800 text-white px-2 py-1 rounded shadow z-10 whitespace-nowrap">
                                No permission to select products
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm font-medium text-gray-900">
                        {product.sku}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900 max-w-md truncate">
                        {product.title}
                      </td>
                      <td
                        className={`py-4 px-4 text-sm ${
                          assignments[product.sku]?.productivity === "Fast" ||
                          product.productivity_level === "Fast"
                            ? "text-green-600"
                            : assignments[product.sku]?.productivity ===
                                  "Medium" ||
                                product.productivity_level === "Medium"
                              ? "text-yellow-600"
                              : assignments[product.sku]?.productivity ===
                                    "Slow" ||
                                  product.productivity_level === "Slow"
                                ? "text-red-600"
                                : "text-gray-900"
                        }`}
                      >
                        {assignments[product.sku]?.productivity ||
                          product.productivity_level ||
                          "-"}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {assignments[product.sku]?.cleaning ||
                          product.cleaning_level ||
                          "-"}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {assignments[product.sku]?.machineTool ||
                          product.machineTool ||
                          "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <EmptyDocumentIcon className="w-12 h-12 text-gray-300" />
                        <span className="text-lg font-medium">
                          No products found
                        </span>
                        <span className="text-sm">
                          Try adjusting your search filters
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-3">
            {paginatedProducts.length > 0 ? (
              paginatedProducts.map((product) => (
                <div
                  key={product.sku}
                  className={`p-4 rounded-xl border ${
                    selectedProducts.includes(product.sku)
                      ? "bg-blue-50 border-blue-200"
                      : "bg-white border-gray-200"
                  } transition-all duration-200 hover:shadow-md`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.sku)}
                      onChange={() => handleCheckboxChange(product.sku)}
                      disabled={!canManageProductConfig}
                      className={`h-5 w-5 mt-0.5 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0 ${
                        !canManageProductConfig
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <h5 className="font-semibold text-gray-900 text-sm truncate">
                        {product.title}
                      </h5>
                      <p className="text-gray-600 text-xs mt-1 truncate">
                        {product.sku}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          assignments[product.sku]?.productivity === "Fast" ||
                          product.productivity_level === "Fast"
                            ? "text-green-600"
                            : assignments[product.sku]?.productivity ===
                                  "Medium" ||
                                product.productivity_level === "Medium"
                              ? "text-yellow-600"
                              : assignments[product.sku]?.productivity ===
                                    "Slow" ||
                                  product.productivity_level === "Slow"
                                ? "text-red-600"
                                : "text-gray-600"
                        }`}
                      >
                        Productivity:{" "}
                        {assignments[product.sku]?.productivity ||
                          product.productivity_level ||
                          "-"}
                      </p>
                      <p className="text-gray-600 text-xs mt-1">
                        Cleaning:{" "}
                        {assignments[product.sku]?.cleaning ||
                          product.cleaning_level ||
                          "-"}
                      </p>
                      <p className="text-gray-600 text-xs mt-1">
                        Machine Tool:{" "}
                        {assignments[product.sku]?.machineTool ||
                          product.machineTool ||
                          "-"}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <EmptyDocumentIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No products found</p>
                <p className="text-sm mt-1">
                  Try adjusting your search filters
                </p>
              </div>
            )}
          </div>

          {filteredProducts.length > itemsPerPage && renderPagination()}
        </>
      )}
    </div>
  );
};

export default ProductList;
